import axios from "axios";
import { UrlApi } from "../../../../../services/apirest";

const UrlRRHH2 = UrlApi + "/rrhh2/legajos";

// Helper function to get the current token from localStorage at the moment of the request.
// This ensures that login/logout events are reflected without needing a page refresh (F5).
const getToken = () => localStorage.getItem("token");

// Standardized error handler for axios errors
const handleAxiosError = (error) => {
  if (error.response) {
    if (error.response.data && error.response.data.error) {
      return { error: error.response.data.error };
    }
    return { error: `Error ${error.response.status}: Error de servidor.` };
  } else if (error.request) {
    return { error: "Error de red. El servidor no responde." };
  }
  return { error: error.message };
};

/**
 * EMPLEADOS
 */

export const createEmpleado = async (data) => {
  try {
    const response = await fetch(UrlRRHH2 + "/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${getToken()}`,
      },
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || `Error ${response.status}: Error de servidor.`);
    }
    return await response.json();
  } catch (error) {
    return { error: error.message };
  }
};

export const updateEmpleado = async (cuil, data) => {
  try {
    const response = await axios.put(`${UrlRRHH2}/${cuil}`, data, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const updateEstado = async (cuil, data) => {
  try {
    const response = await axios.put(`${UrlRRHH2}/estado/${cuil}`, data, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const getAllEmpleados = async (search, rowsPerPage, page) => {
  try {
    const url = new URL(UrlRRHH2 + "/all");
    url.searchParams.append("search", search || "");
    url.searchParams.append("rowsPerPage", rowsPerPage || 10);
    url.searchParams.append("page", page || 1);
    const response = await axios.get(url.toString(), {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return response.data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const fetchEmpleadoByCUIL = async (cuil) => {
  try {
    const { data } = await axios.get(`${UrlRRHH2}/${cuil}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

/**
 * PERSONALES & SUPPORT DATA
 */

export const fetchLocalidadesPorProvincia = async (idProvincia) => {
  try {
    const { data } = await axios.get(
      `${UrlApi}/localidades/provincia/${idProvincia}`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const fetchProvincias = async () => {
  try {
    const { data } = await axios.get(`${UrlApi}/provincias`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const fetchTypesDoc = async () => {
  try {
    const { data } = await axios.get(`${UrlRRHH2}/type-doc`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const fetchEstadoCivil = async () => {
  try {
    const { data } = await axios.get(`${UrlRRHH2}/estados-civiles`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const fetchPaises = async () => {
  try {
    const { data } = await axios.get(`${UrlRRHH2}/paises`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const fetchSucursales = async () => {
  try {
    const { data } = await axios.get(`${UrlApi}/sucursales`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const fetchTelefonosTipos = async () => {
  try {
    const { data } = await axios.get(`${UrlRRHH2}/telefonos-tipos`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const deleteTelefono = async (cuilEmpleado, idTelefono) => {
  try {
    const { data } = await axios.delete(
      `${UrlRRHH2}/${cuilEmpleado}/telefonos/${idTelefono}`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

/**
 * FAMILIARES
 */

export const fetchParentescos = async () => {
  try {
    const { data } = await axios.get(`${UrlRRHH2}/parentescos`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const deleteFamiliar = async (cuilEmpleado, idFamiliar) => {
  try {
    const { data } = await axios.delete(
      `${UrlRRHH2}/${cuilEmpleado}/familiares/${idFamiliar}`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const fetchEstudiosAcademicos = async () => {
  try {
    const { data } = await axios.get(`${UrlRRHH2}/estudios-academicos`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

/**
 * LABORALES reference data
 */

export const fetchCondicion = async () => {
  try {
    const { data } = await axios.get(`${UrlRRHH2}/condiciones`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const fetchPuestos = async () => {
  try {
    const { data } = await axios.get(`${UrlApi}/sp_puestos`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const fetchSectores = async () => {
  try {
    const { data } = await axios.get(`${UrlApi}/sectores/tabla`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const fetchSindicatos = async () => {
  try {
    const { data } = await axios.get(`${UrlRRHH2}/sindicatos`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const fetchObrasSocialesByEmpresa = async (idEmpresa) => {
  try {
    const { data } = await axios.get(`${UrlRRHH2}/obras-sociales/${idEmpresa}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const fetchTareasByEmpresa = async (idEmpresa) => {
  try {
    const { data } = await axios.get(`${UrlRRHH2}/tareas/${idEmpresa}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const fetchBancos = async () => {
  try {
    const { data } = await axios.get(`${UrlApi}/bancos`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

/**
 * USUARIOS DEL SISTEMA
 */

export const fetchUsuarios = async () => {
  try {
    const { data } = await axios.get(`${UrlApi}/userdata`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

/**
 * CONVENIOS CRUD
 */

export const fetchConvenios = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = `${UrlRRHH2}/convenios/all${queryParams ? `?${queryParams}` : ""}`;
    const { data } = await axios.get(url, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const createConvenio = async (convenio) => {
  const response = await fetch(`${UrlRRHH2}/convenios/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${getToken()}`,
    },
    body: JSON.stringify(convenio),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Error al crear convenio");
  }
  return await response.json();
};

export const updateConvenio = async (idConvenio, convenio) => {
  try {
    const { data } = await axios.put(`${UrlRRHH2}/convenios/${idConvenio}`, convenio, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const deleteConvenio = async (idConvenio) => {
  try {
    const { data } = await axios.delete(`${UrlRRHH2}/convenios/${idConvenio}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

/**
 * CATEGORIAS CRUD
 */

export const fetchCategoriasByEmpresa = async (idEmpresa) => {
  try {
    const { data } = await axios.get(`${UrlRRHH2}/categorias/${idEmpresa}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const fetchCategorias = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = `${UrlRRHH2}/categorias/all${queryParams ? `?${queryParams}` : ""}`;
    const { data } = await axios.get(url, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const createCategoria = async (categoria) => {
  const response = await fetch(`${UrlRRHH2}/categorias/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${getToken()}`,
    },
    body: JSON.stringify(categoria),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Error al crear categoría");
  }
  return await response.json();
};

export const updateCategoria = async (idCategoria, categoria) => {
  try {
    const { data } = await axios.put(`${UrlRRHH2}/categorias/${idCategoria}`, categoria, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const deleteCategoria = async (idCategoria) => {
  try {
    const { data } = await axios.delete(`${UrlRRHH2}/categorias/${idCategoria}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

/**
 * OBRAS SOCIALES CRUD
 */

export const fetchObrasSociales = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = `${UrlRRHH2}/obras-sociales/all${queryParams ? `?${queryParams}` : ""}`;
    const { data } = await axios.get(url, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const createObraSocial = async (os) => {
  const response = await fetch(`${UrlRRHH2}/obras-sociales/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${getToken()}`,
    },
    body: JSON.stringify(os),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Error al crear obra social");
  }
  return await response.json();
};

export const updateObraSocial = async (idOS, os) => {
  try {
    const { data } = await axios.put(`${UrlRRHH2}/obras-sociales/${idOS}`, os, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const deleteObraSocial = async (idOS) => {
  try {
    const { data } = await axios.delete(`${UrlRRHH2}/obras-sociales/${idOS}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

/**
 * TAREAS CRUD
 */

export const fetchTareas = async (params = {}) => {
  try {
    const queryParams = new URLSearchParams(params).toString();
    const url = `${UrlRRHH2}/tareas/all${queryParams ? `?${queryParams}` : ""}`;
    const { data } = await axios.get(url, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const createTarea = async (tarea) => {
  const response = await fetch(`${UrlRRHH2}/tareas/`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getToken()}`,
    },
    body: JSON.stringify(tarea),
  });
  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Error al crear tarea");
  }
  return await response.json();
};

export const updateTarea = async (idTarea, tarea) => {
  try {
    const { data } = await axios.put(`${UrlRRHH2}/tareas/${idTarea}`, tarea, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const deleteTarea = async (idTarea) => {
  try {
    const { data } = await axios.delete(`${UrlRRHH2}/tareas/${idTarea}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

/**
 * EMPRESAS & SUCURSALES
 */

export const fetchEmpresas = async () => {
  try {
    const { data } = await axios.get(`${UrlApi}/sucursalesCRUD/empresas/get`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

/**
 * ARCHIVOS & DOCUMENTACION
 */

export const getFotoEmpleado = async (id) => {
  try {
    const { data } = await axios.get(`${UrlRRHH2}/foto/${id}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const getArchivoLaboral = async (uuid_archivo) => {
  try {
    const { data } = await axios.get(`${UrlRRHH2}/archivo/${uuid_archivo}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    });
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};

export const getArchivo = async (filename) => {
  try {
    const { data } = await axios.get(
      `${UrlApi}/uploadfiles/download/${filename}`,
      { headers: { Authorization: `Bearer ${getToken()}` } }
    );
    return data;
  } catch (error) {
    return handleAxiosError(error);
  }
};
